import LoggerService from '../../../../services/logger.service'
import { IndexDbService, DB_NAME_SESSION_PERSISTENCE } from '../../../../services/indexDb.service'
import { ISessionUser } from '../../resume/store/sessionResume.types'

const DB_NAME = DB_NAME_SESSION_PERSISTENCE
const TABLE_NAME = 'persistedMetadata'
const METADATA_KEY = 'sessionMetadata'

export interface IPersistedMetadata {
  user?: ISessionUser
  currentRoutePath?: string
  pcrId?: string
  timestamp: number
}

interface PersistedMetadataDoc {
  id: string
  metadata: string
  timestamp: number
}

class PersistSessionDb extends IndexDbService {
  constructor() {
    super(DB_NAME)

    this.db.version(1).stores({
      persistedMetadata: 'id',
    })

    this.db.open()
  }
}

class SessionPersistService {
  private db: PersistSessionDb

  constructor() {
    this.db = new PersistSessionDb()
  }

  private async ensureDbOpen() {
    try {
      if (!this.db.db.isOpen()) {
        await this.db.db.open()
      }
    } catch (error) {
      LoggerService.error(`[SessionPersist] Error opening database: ${error}`)
      throw error
    }
  }

  async saveMetadata(metadata: IPersistedMetadata) {
    try {
      await this.ensureDbOpen()

      const metadataString = JSON.stringify(metadata)

      const metadataDoc: PersistedMetadataDoc = {
        id: METADATA_KEY,
        metadata: metadataString,
        timestamp: Date.now(),
      }

      await this.db.save(metadataDoc, TABLE_NAME)
      LoggerService.info('[SessionPersist] Metadata persisted in IndexedDB')
    } catch (error) {
      LoggerService.error(`[SessionPersist] Error saving metadata: ${error}`)
    }
  }

  async loadMetadata(): Promise<IPersistedMetadata | null> {
    try {
      await this.ensureDbOpen()
      const metadataDoc = await this.db.get<PersistedMetadataDoc>(METADATA_KEY, TABLE_NAME)

      if (!metadataDoc) {
        return null
      }

      let metadata: IPersistedMetadata
      try {
        metadata = JSON.parse(metadataDoc.metadata)
      } catch (error) {
        LoggerService.error(`[SessionPersist] Error parsing metadata: ${error}`)
        return null
      }

      return metadata
    } catch (error) {
      LoggerService.error(`[SessionPersist] Error loading metadata: ${error}`)
      return null
    }
  }

  async clearMetadata() {
    try {
      await this.ensureDbOpen()
      await this.db.remove(METADATA_KEY, TABLE_NAME)
      LoggerService.info('[SessionPersist] Metadata cleared from IndexedDB')
    } catch (error) {
      LoggerService.error(`[SessionPersist] Error clearing metadata: ${error}`)
    }
  }

  async hasMetadata(): Promise<boolean> {
    try {
      await this.ensureDbOpen()
      const metadataDoc = await this.db.get<PersistedMetadataDoc>(METADATA_KEY, TABLE_NAME)
      return !!metadataDoc
    } catch (error) {
      LoggerService.error(`[SessionPersist] Error checking metadata: ${error}`)
      return false
    }
  }
}

export const sessionPersistService = new SessionPersistService()
